import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import DataTable from "@/components/Datatable/Datatable";
import { Column } from "@/components/Datatable/types";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { ChipFilterGroup } from "@/components/ChipFilterGroup/ChipFilterGroup";
import { BillingInvoice } from "../../api/getBillingInvoices/types";
import { useBillingInvoicesPage } from "../../hooks/useBillingInvoicesPage";
import BillingInvoiceDrawer from "../BillingInvoiceDrawer/BillingInvoiceDrawer";
import {
  invoiceActionsColumn,
  invoiceAmountColumn,
  invoiceBalanceColumn,
  invoiceDateColumn,
  invoiceNumberColumn,
  invoiceStatusColumn,
} from "../../config/billingInvoiceColumns";
import { formatBillingSubscriptionName } from "../../utils/billingFormatters";

const subscriptionColumn: Column<BillingInvoice> = {
  header: "Subscription",
  accessorKey: "subscription",
  enableSorting: true,
  cell: ({ row }) => {
    const { subscription } = row.original;
    if (!subscription) return "—";

    return (
      <Link
        className="text-gray-900 underline-offset-2 hover:underline dark:text-white"
        to={`/hub/billing/subscriptions/${subscription.id}/invoices`}
      >
        {formatBillingSubscriptionName(
          subscription.productName,
          subscription.trade,
        )}
      </Link>
    );
  },
};

const BillingInvoicesPage: React.FC = () => {
  const {
    loading,
    filterConfigs,
    filterValues,
    handleFilterChange,
    handleClearFilters,
    noDataMessage,
    sorting,
    handleSortingChange,
    pageRows,
    totalCount,
    pageIndex,
    pageSize,
    handlePageChange,
    invoiceDrawer,
    invoicePdfDownload,
  } = useBillingInvoicesPage();

  const columns = useMemo(
    () => [
      invoiceNumberColumn(invoiceDrawer.openInvoice),
      subscriptionColumn,
      invoiceDateColumn,
      invoiceAmountColumn,
      invoiceBalanceColumn,
      invoiceStatusColumn,
      invoiceActionsColumn({
        onViewInvoice: invoiceDrawer.openInvoice,
        onDownloadPdf: invoicePdfDownload.downloadInvoicePdf,
        downloadingInvoiceId: invoicePdfDownload.downloadingInvoiceId,
      }),
    ],
    [
      invoiceDrawer.openInvoice,
      invoicePdfDownload.downloadInvoicePdf,
      invoicePdfDownload.downloadingInvoiceId,
    ],
  );

  return (
    <MainPageWrapper title="Invoices">
      <div className="mb-4 rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
        <ChipFilterGroup
          filters={filterConfigs}
          onChange={handleFilterChange}
          onClearAll={handleClearFilters}
          showActiveFilters={true}
          showChips={true}
          values={filterValues}
        />
      </div>
      <DataTable<BillingInvoice>
        columns={columns}
        data={pageRows}
        loading={loading}
        noDataMessage={noDataMessage}
        onPageChange={handlePageChange}
        onSortingChange={handleSortingChange}
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowKeyExtractor={(invoice) => invoice.id}
        sorting={sorting}
        totalCount={totalCount}
      />
      <BillingInvoiceDrawer
        downloadingInvoiceId={invoicePdfDownload.downloadingInvoiceId}
        invoice={invoiceDrawer.invoice}
        loading={invoiceDrawer.loading}
        onClose={invoiceDrawer.closeInvoice}
        onDownloadPdf={invoicePdfDownload.downloadInvoicePdf}
        open={invoiceDrawer.isOpen}
      />
    </MainPageWrapper>
  );
};

export default BillingInvoicesPage;
